import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AgrNotificationEmptyModule } from '@next/core-lib/components';
import { AgrExportModule } from '@next/core-lib/export';
import { AgrFormModule } from '@next/core-lib/form';
import { AgrI18nModule, AgrI18nSharedModule, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { TableV2Module } from '@next/core-lib/table-v2';
import { Location, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { firstValueFrom, Observable, finalize, Subject, takeUntil } from 'rxjs';

import { COW_TABLE_COLUMNS, MEASUREMENT_FORM } from './cow-table.constants';

import { CowTablePrintComponent } from './print-view/cow-table-print.component';
import { stepValidator } from '../../../shared/validators';
import { CowDataFilters, CowDataSource } from '../../datasource/cow.datasource';
import { CowWithMeasurement } from '../../models/cow.model';
import { Measurement } from '../../models/measurement.model';
import { CowService } from '../../services/cow.service';

@Component({
  selector: 'cow-table',
  templateUrl: './cow-table.component.html',
  styleUrls: ['./cow-table.component.scss'],
  standalone: true,
  imports: [
    TableV2Module,
    AgrI18nSharedModule,
    AgrI18nModule,
    AgrFormModule,
    AgrExportModule,
    AgrNotificationEmptyModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    CowTablePrintComponent,
    RouterModule,
  ],
  providers: [TranslatePipe],
})
export class CowTableComponent implements OnInit, OnDestroy {
  protected readonly cowService: CowService = inject(CowService);
  protected readonly translatePipe: TranslatePipe = inject(TranslatePipe);
  private readonly store: Store = inject(Store);
  @Input() public ReadOnlyMode = false;
  @Input() public selectedMeasurementDate: Date;
  @Output() measurementDateChange = new EventEmitter<Date>();

  protected dataSource: CowDataSource;
  protected readonly maxDate: Date = new Date();
  protected get displayColumns(): string[] {
    return this.ReadOnlyMode ? [...COW_TABLE_COLUMNS] : [...COW_TABLE_COLUMNS, 'actions'];
  }
  protected availableDates = this.cowService.getAvailableDates();

  protected measurementForms: Map<string, FormGroup> = new Map<string, FormGroup>();
  protected manualBcsChanges: Set<string> = new Set<string>();
  protected isSaving = signal(false);
  protected hasChanges = signal(false);
  protected sortColumn: WritableSignal<'earNumber' | 'ageInMonths' | null> = signal(null);
  protected sortDirection: WritableSignal<'asc' | 'desc' | null> = signal(null);

  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  location$: Observable<Location> = this.store.pipe(select(UserSelectors.selectLocation));
  readonly searchAndFilters = new FormGroup({
    measurementDate: new FormControl<Date>(new Date()),
    allowEmptyMeasurements: new FormControl<boolean>(this.ReadOnlyMode),
  });

  private readonly destroy$ = new Subject<void>();

  printTableData: CowWithMeasurement[] = [];
  measurementDateForPrint: Date;
  location: Location;

  ngOnInit(): void {
    this.dataSource = new CowDataSource(this.cowService);

    this.cowService.loadMeasurementDates().pipe(takeUntil(this.destroy$)).subscribe();

    if (this.ReadOnlyMode) {
      this.initReadOnlyMode();
    }

    if (this.selectedMeasurementDate) {
      this.searchAndFilters
        .get(MEASUREMENT_FORM.MEASUREMENT_DATE)
        .setValue(this.selectedMeasurementDate);
    }

    this.searchAndFilters
      .get(MEASUREMENT_FORM.MEASUREMENT_DATE)
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((date) => {
        this.measurementDateChange.emit(date);
      });

    this.dataSource.applyFilters(this.searchAndFilters.value as CowDataFilters);
    this.searchAndFilters.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dataSource.applyFilters(this.searchAndFilters.value as CowDataFilters);
    });

    this.dataSource
      .connect(null)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cows) => {
        this.initializeMeasurementForms(cows);
      });
  }

  private initReadOnlyMode(): void {
    this.searchAndFilters.get('allowEmptyMeasurements')?.setValue(true);
  }

  private createMeasurementFormGroup(cow?: CowWithMeasurement): FormGroup {
    return new FormGroup({
      hipHeight: new FormControl<number | null>(cow?.heightCm ?? null, [
        Validators.min(75),
        Validators.max(175),
      ]),
      bodyConditionScore: new FormControl<number | null>(cow?.bodyConditionScore ?? null, [
        Validators.min(1),
        Validators.max(5),
        stepValidator(0.5),
      ]),
    });
  }

  private initializeMeasurementForms(cows: readonly CowWithMeasurement[]): void {
    this.measurementForms.clear();
    this.manualBcsChanges.clear();
    cows.forEach((cow) => {
      const form = this.createMeasurementFormGroup(cow);
      this.measurementForms.set(cow.cowId, form);

      if (cow.bodyConditionScore !== null && cow.bodyConditionScore !== undefined) {
        this.manualBcsChanges.add(cow.cowId);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected getMeasurementForm(cow: CowWithMeasurement): FormGroup | undefined {
    return this.measurementForms.get(cow.cowId);
  }

  protected resetFilters(): void {
    this.searchAndFilters.get(MEASUREMENT_FORM.MEASUREMENT_DATE)?.setValue(new Date());
  }

  protected async printTable(): Promise<void> {
    const rows = await firstValueFrom(this.dataSource.results$);
    this.location = await firstValueFrom(this.location$);

    this.printTableData = [...rows] as CowWithMeasurement[];
    this.measurementDateForPrint = this.searchAndFilters.get(
      MEASUREMENT_FORM.MEASUREMENT_DATE,
    )?.value;

    this.cdr.detectChanges();
    globalThis.print();
  }

  protected cancelMeasurements(): void {
    this.hasChanges.set(false);
    void firstValueFrom(this.dataSource.getCows$()).then((cows) => {
      this.initializeMeasurementForms(cows);
    });
  }

  protected deleteMeasurement(cow: CowWithMeasurement): void {
    const form = this.measurementForms.get(cow.cowId);
    if (form) {
      form.reset();
    }

    this.manualBcsChanges.delete(cow.cowId);

    this.hasChanges.set(true);
  }

  protected onHipHeightChange(cow: CowWithMeasurement, value: number | null): void {
    const form = this.measurementForms.get(cow.cowId);
    if (!form || !value) return;

    const hipHeightControl = form.get(MEASUREMENT_FORM.HIP_HEIGHT);
    if (hipHeightControl?.invalid) return;

    // Set default BCS if not manually changed
    const bcsControl = form.get(MEASUREMENT_FORM.BODY_CONDITION_SCORE);
    if (
      !this.manualBcsChanges.has(cow.cowId) &&
      (bcsControl?.value === null || bcsControl?.value === undefined)
    ) {
      const defaultBcs = this.getDefaultBCS(cow.ageInMonths);
      bcsControl?.setValue(defaultBcs, { emitEvent: false });
    }

    this.hasChanges.set(true);
  }

  protected onBcsChange(cow: CowWithMeasurement): void {
    const form = this.measurementForms.get(cow.cowId);
    if (!form) return;

    const bcsControl = form.get(MEASUREMENT_FORM.BODY_CONDITION_SCORE);
    if (bcsControl?.invalid) return;

    this.manualBcsChanges.add(cow.cowId);
    this.hasChanges.set(true);
  }

  protected showSuccessToast(): void {
    const message = this.translatePipe.transform('height-measurement.messages.saveSuccess');
    this.store.dispatch(
      NotificationActions.successSnackbar({
        message,
      }),
    );
  }

  protected showErrorToastWithRetry(): void {
    const message = this.translatePipe.transform('height-measurement.messages.saveFailed');
    this.store.dispatch(
      NotificationActions.errorSnackbar({
        message,
      }),
    );
  }

  protected saveAllMeasurements(): void {
    const measurements = this.getAllMeasurementsFromForm();

    this.isSaving.set(true);
    const measurementDate = this.searchAndFilters.get(MEASUREMENT_FORM.MEASUREMENT_DATE).value;

    this.cowService
      .batchSaveCowMeasurements(measurements, measurementDate)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.showSuccessToast();
          this.reloadDataSource();
          this.cowService.refreshMeasurementDates();
        },
        error: () => {
          this.showErrorToastWithRetry();
        },
      });
  }

  private reloadDataSource(): void {
    this.hasChanges.set(false);
    this.dataSource.applyFilters(this.searchAndFilters.value as CowDataFilters);
  }

  private getAllMeasurementsFromForm(): Measurement[] {
    const measurements: Measurement[] = [];

    this.measurementForms.forEach((form, cowId) => {
      const hipHeight = form.get(MEASUREMENT_FORM.HIP_HEIGHT)?.value;
      const bodyConditionScore = form.get(MEASUREMENT_FORM.BODY_CONDITION_SCORE)?.value;

      if (hipHeight !== null && hipHeight !== undefined) {
        measurements.push({
          cowId,
          heightCm: hipHeight,
          bodyConditionScore: bodyConditionScore ?? undefined,
        });
      }
    });

    return measurements;
  }

  protected getDefaultBCS(age: number): number {
    if (age < 6) {
      return 2;
    }

    if (age >= 6 && age < 12) {
      return 2.5;
    }

    if (age >= 12) {
      return 3;
    }

    return 2;
  }

  protected isBcsManuallyChanged(cow: CowWithMeasurement): boolean {
    return this.manualBcsChanges.has(cow.cowId);
  }

  protected hasInvalidForms(): boolean {
    for (const form of this.measurementForms.values()) {
      const hipHeight = form.get(MEASUREMENT_FORM.HIP_HEIGHT);
      const bcs = form.get(MEASUREMENT_FORM.BODY_CONDITION_SCORE);

      const hasHipHeight = hipHeight?.value !== null && hipHeight?.value !== undefined;
      const hasBcs = bcs?.value !== null && bcs?.value !== undefined;

      if (hasBcs && !hasHipHeight) {
        return true;
      }

      if (hasHipHeight) {
        if (hipHeight.invalid || bcs?.invalid) {
          return true;
        }
      }
    }
    return false;
  }

  protected sortData(column: 'earNumber' | 'ageInMonths'): void {
    if (this.sortColumn() === column) {
      // Toggle direction: asc -> desc -> null
      if (this.sortDirection() === 'asc') {
        this.sortDirection.set('desc');
      } else if (this.sortDirection() === 'desc') {
        this.sortDirection.set(null);
        this.sortColumn.set(null);
      }
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }

    this.dataSource.setSorting(this.sortColumn(), this.sortDirection());
  }

  protected datepickerDateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      return this.hasMeasurements(cellDate) ? 'datepicker-custom-date-class' : '';
    }

    return '';
  };

  private hasMeasurements(date: Date): boolean {
    if (date === null || date === undefined) {
      return false;
    }

    const targetDate = this.normalizeDate(date);
    return this.availableDates().some(
      (d) => this.normalizeDate(d).toISOString() === targetDate.toISOString(),
    );
  }

  private normalizeDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
  }
}
